import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditLeadDialog = ({ lead, open, onClose, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [companies, setCompanies] = useState([]);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCreatingNewCompany, setIsCreatingNewCompany] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.raw_data?.name || "",
        email: lead.raw_data?.email || "",
        phone: lead.raw_data?.phone || "",
        company: lead.raw_data?.company || "",
        title: lead.raw_data?.title || "",
        stage: lead.stage || "new",
        lead_date: lead.lead_date || ""
      });
      
      // Check if lead already has a company
      if (lead.raw_data?.company && !lead.company_id) {
        setIsCreatingNewCompany(true);
      }
    }
  }, [lead]);

  useEffect(() => {
    if (open) {
      fetchCompanies();
    }
  }, [open]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/companies`, {
        withCredentials: true
      });
      const companiesData = Array.isArray(response.data) ? response.data : response.data.companies || [];
      setCompanies(companiesData);
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setFormData({
      ...formData,
      company: company.name
    });
    setCompanyOpen(false);
    setIsCreatingNewCompany(false);
  };

  const handleCreateNewCompany = () => {
    setSelectedCompany(null);
    setIsCreatingNewCompany(true);
    setFormData({
      ...formData,
      company: ""
    });
    setCompanyOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(`${API}/leads/${lead.id}`, {
        raw_data: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          title: formData.title
        },
        stage: formData.stage,
        lead_date: formData.lead_date || null
      });
      toast.success("Lead updated!");
      onClose();
      onUpdated();
    } catch (error) {
      toast.error("Failed to update lead");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div className="grid gap-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
            </div>
            <div className="grid gap-2">
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div className="grid gap-2">
              <Label>Company</Label>
              <div className="space-y-2">
                {!isCreatingNewCompany ? (
                  <div className="flex gap-2">
                    <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={companyOpen}
                          className="flex-1 justify-between"
                        >
                          {selectedCompany?.name || formData.company || "Select existing company..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0">
                        <Command>
                          <CommandInput 
                            placeholder="Search companies..." 
                            value={companySearch}
                            onValueChange={setCompanySearch}
                          />
                          <CommandEmpty>
                            No company found.
                          </CommandEmpty>
                          <CommandGroup className="max-h-64 overflow-auto">
                            <CommandItem
                              onSelect={handleCreateNewCompany}
                              className="flex items-center gap-2 text-blue-600 font-medium"
                            >
                              <Plus className="h-4 w-4" />
                              <span>Create new company</span>
                            </CommandItem>
                            {companies.map((company) => (
                              <CommandItem
                                key={company.id}
                                onSelect={() => handleCompanySelect(company)}
                                className="flex items-center justify-between"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{company.name}</span>
                                  {company.industry && (
                                    <span className="text-xs text-slate-500">{company.industry}</span>
                                  )}
                                </div>
                                {selectedCompany?.id === company.id && (
                                  <Check className="h-4 w-4 text-blue-600" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleCreateNewCompany}
                      title="Create new company"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Creating new company</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIsCreatingNewCompany(false);
                          setFormData({...formData, company: ""});
                        }}
                      >
                        Select existing instead
                      </Button>
                    </div>
                    <Input 
                      value={formData.company} 
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      placeholder="Enter new company name..."
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Title</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
            </div>
            <div className="grid gap-2">
              <Label>Stage</Label>
              <Select value={formData.stage} onValueChange={(value) => setFormData({...formData, stage: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="negotiating">Negotiating</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Lead Date (Optional)</Label>
              <Input 
                type="date" 
                value={formData.lead_date} 
                onChange={(e) => setFormData({...formData, lead_date: e.target.value})}
              />
              <p className="text-xs text-slate-500">
                Optional — the date this lead was created/received
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" disabled={loading}>{loading ? "Updating..." : "Update Lead"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditLeadDialog;
